import { Link } from 'react-router-dom';
import {
    Header,
    Form,
    FormField,
    Dropdown,
    Input,
    TextArea,
    Button,
    Message,
    Segment,
    Radio,
    Divider,
    Comment,
    CommentGroup,
    CommentContent,
    CommentAuthor,
    CommentMetadata,
    CommentText,
    Icon,
} from 'semantic-ui-react';
import { LoadingWrapper } from '../../common';
import { IcctDatePicker } from '../../common/IcctDatePicker';
import { ResolveRoadblockModal } from './ResolveRoadblockModal';
import { DeleteRoadblockModal } from './DeleteRoadblockModal';
import { formatDateToMmDdYyyy } from '../../../utils/formatDate';
import { useRoadblockDetail } from './hooks';
import { roadblockTypePillStyle, TIME_UNIT_OPTIONS } from './constants';
import { useAuth } from '../../auth';

const DECIMAL_RE = /^\d*\.?\d*$/;

const LABEL_STYLE = {
    color: '#4a6fa5',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    minWidth: '14rem',
};

const statusBadgeStyle = (isOpen) => ({
    display: 'inline-block',
    padding: '0.2em 0.6em',
    borderRadius: '0.25rem',
    fontSize: '0.7rem',
    fontWeight: '600',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    verticalAlign: 'middle',
    marginLeft: '0.3rem',
    color: isOpen ? '#1558b0' : '#3d7a4f',
    background: isOpen ? '#ddeeff' : '#d6f0de',
});

const DetailRow = ({ label, align = 'baseline', children }) => (
    <div style={{ display: 'flex', alignItems: align, gap: '0.5rem' }}>
        <label style={LABEL_STYLE}>{label}</label>
        <div style={{ minWidth: 0, overflowWrap: 'break-word' }}>{children}</div>
    </div>
);

export const RoadblockDetail = () => {
    const {
        id,
        roadblock,
        typeOptions,
        dfnOptions,
        memberOptions,
        subtypeOptions,
        loaded,
        isEditing,
        setIsEditing,
        editForm,
        saving,
        error,
        setError,
        success,
        setSuccess,
        inputErrors,
        showResolveModal,
        setShowResolveModal,
        showDeleteModal,
        setShowDeleteModal,
        deleting,
        noteText,
        setNoteText,
        savingNote,
        noteError,
        maxNote,
        handleEditChange,
        handleSave,
        handleCancelEdit,
        handleAddNote,
        handleResolved,
        canDelete,
        handleDelete,
    } = useRoadblockDetail();

    const { isReadOnly } = useAuth();

    if (!loaded) return <LoadingWrapper loading />;
    if (!roadblock) return <Message negative>{error || 'Roadblock not found.'}</Message>;

    const isOpen = roadblock.status === 'open';
    const isOtherType = typeOptions.find(t => t.value === roadblock.roadblockType)?.requiresDescription ?? false;
    const typeLabel = roadblock.roadblockTypeDisplay ?? '';

    return (
        <div style={{ padding: '1.5rem', maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <Link to="/forms/roadblock">
                    <Icon name="arrow left" />
                    Back to Roadblocks
                </Link>
            </div>

            {error && <Message negative onDismiss={() => setError('')}>{error}</Message>}
            {success && <Message positive onDismiss={() => setSuccess('')}>{success}</Message>}

            <Segment style={{ textAlign: 'left', padding: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Header as="h3" style={{ margin: 0 }}>
                        {isEditing ? `Edit Roadblock ID${id}` : `Roadblock ID ${id}`}
                        {!isEditing && (
                            <span style={statusBadgeStyle(isOpen)}>
                                {isOpen ? 'Open' : 'Resolved'}
                            </span>
                        )}
                    </Header>
                    {!isEditing && !isReadOnly && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {isOpen && (
                                <Button basic color="green" size="small" onClick={() => setShowResolveModal(true)}>
                                    <Icon name="check" />
                                    Resolve
                                </Button>
                            )}
                            <Button basic color="blue" size="small" onClick={() => setIsEditing(true)}>
                                <Icon name="edit" />
                                Edit
                            </Button>
                            {canDelete(roadblock) && (
                                <Button basic color="red" size="small" onClick={() => setShowDeleteModal(true)}>
                                    <Icon name="trash" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                <Divider />

                {isEditing ? (
                    <Form>
                        <FormField>
                            <label>Team Name</label>
                            <div style={{ padding: '0.5rem 0', color: '#555' }}>{roadblock.teamName}</div>
                        </FormField>

                        <FormField error={!!inputErrors.date}>
                            <label>Date of Roadblock Start</label>
                            <IcctDatePicker
                                keyName="date"
                                value={editForm.date}
                                onChange={handleEditChange}
                                error={!!inputErrors.date}
                            />
                            {inputErrors.date && (
                                <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                    {inputErrors.date}
                                </div>
                            )}
                        </FormField>

                        <FormField>
                            <label>Roadblock Type</label>
                            <div style={{ padding: '0.5rem 0', color: '#555' }}>{typeLabel}</div>
                        </FormField>

                        {subtypeOptions.length > 0 && (
                            <FormField error={!!inputErrors.roadblockSubtype}>
                                <label>Subtype <span style={{ color: '#999', fontWeight: 'normal' }}>(optional)</span></label>
                                <Dropdown
                                    search
                                    selection
                                    clearable
                                    placeholder="Select a subtype..."
                                    options={subtypeOptions}
                                    value={editForm.roadblockSubtype ?? ''}
                                    onChange={(e, { value }) => handleEditChange('roadblockSubtype', value || null)}
                                />
                                {inputErrors.roadblockSubtype && (
                                    <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                        {inputErrors.roadblockSubtype}
                                    </div>
                                )}
                            </FormField>
                        )}

                        {isOtherType && (
                            <FormField>
                                <label>Other — Please Specify</label>
                                <div style={{ padding: '0.75rem', border: '1px solid rgba(34,36,38,.15)', borderRadius: '0.25rem', color: '#999', background: '#f9f9f9', whiteSpace: 'pre-wrap' }}>
                                    {roadblock.otherDescription || '—'}
                                </div>
                            </FormField>
                        )}

                        <FormField>
                            <label>Production Impact</label>
                            <Form.Group inline style={{ marginTop: '0.5rem' }}>
                                <Radio
                                    label="Yes"
                                    name="production_impact_edit"
                                    checked={editForm.productionImpact === true}
                                    onChange={() => handleEditChange('productionImpact', true)}
                                />
                                <Radio
                                    label="No"
                                    name="production_impact_edit"
                                    style={{ marginLeft: '1rem' }}
                                    checked={editForm.productionImpact === false}
                                    onChange={() => handleEditChange('productionImpact', false)}
                                />
                            </Form.Group>
                        </FormField>

                        <FormField>
                            <label>DFN</label>
                            <Dropdown
                                search
                                selection
                                clearable
                                placeholder="Select a DFN..."
                                options={dfnOptions}
                                value={editForm.dfn ?? ''}
                                onChange={(e, { value }) => handleEditChange('dfn', value || null)}
                            />
                        </FormField>

                        <FormField error={!!inputErrors.assignedTo}>
                            <label>Assigned To <span style={{ color: '#999', fontWeight: 'normal' }}>(optional)</span></label>
                            <Dropdown
                                search
                                selection
                                clearable
                                placeholder="Select a member..."
                                options={memberOptions}
                                value={editForm.assignedTo ?? ''}
                                onChange={(e, { value }) => handleEditChange('assignedTo', value || null)}
                            />
                            {inputErrors.assignedTo && (
                                <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                    {inputErrors.assignedTo}
                                </div>
                            )}
                        </FormField>

                        <FormField>
                            <label>Time Spent to Resolve</label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Input
                                    value={editForm.leaderTimeSpent}
                                    placeholder="e.g., 2, 3.25"
                                    maxLength={6}
                                    style={{ flex: 1 }}
                                    onChange={(e, { value }) => {
                                        if (DECIMAL_RE.test(value)) handleEditChange('leaderTimeSpent', value);
                                    }}
                                />
                                <Dropdown
                                    selection
                                    options={TIME_UNIT_OPTIONS}
                                    value={editForm.leaderTimeUnit || 'hours'}
                                    onChange={(e, { value }) => handleEditChange('leaderTimeUnit', value)}
                                    style={{ minWidth: '7.5rem' }}
                                />
                            </div>
                            <div style={{ color: '#888', fontSize: '0.8em', marginTop: '0.3em' }}>
                                1 day = 8 hrs &nbsp;&middot;&nbsp; 1 week = 40 hrs &nbsp;&middot;&nbsp; 1 month = 160 hrs
                            </div>
                        </FormField>

                        <FormField>
                            <label>Roadblock Description (Required)</label>
                            <div style={{ padding: '0.75rem', border: '1px solid rgba(34,36,38,.15)', borderRadius: '0.25rem', color: '#999', background: '#f9f9f9', whiteSpace: 'pre-wrap', minHeight: '5rem' }}>
                                {roadblock.description}
                            </div>
                        </FormField>

                        <p style={{ color: '#666', fontSize: '0.875rem', marginTop: '1rem' }}>
                            Note: Roadblock Type &amp; Description cannot be edited.
                        </p>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
                            <span style={{ marginRight: 'auto', color: '#aaa', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                Saves field changes only — use "Add Note" below for notes
                            </span>
                            <Button type="button" onClick={handleCancelEdit} disabled={saving}>
                                Cancel
                            </Button>
                            <Button primary type="button" onClick={handleSave} loading={saving} disabled={saving}>
                                Save Changes
                            </Button>
                        </div>
                    </Form>
                ) : (
                    <>
                        <Header as="h5" style={{ marginTop: 0, marginBottom: '1.25rem', color: '#444' }}>
                            Form Information
                        </Header>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <DetailRow label="Team Name:">{roadblock.teamName}</DetailRow>
                            <DetailRow label="Date of Roadblock Start:">{formatDateToMmDdYyyy(roadblock.date)}</DetailRow>
                            <DetailRow label="Roadblock Type:" align="center">
                                <span style={roadblockTypePillStyle(roadblock.roadblockType)}>
                                    {typeLabel}
                                </span>
                            </DetailRow>
                            {roadblock.roadblockSubtypeLabel && (
                                <DetailRow label="Subtype:">
                                    {roadblock.roadblockSubtypeLabel}
                                </DetailRow>
                            )}
                            {isOtherType && (
                                <DetailRow label="Other — Please Specify:" align="flex-start">
                                    {roadblock.otherDescription || '—'}
                                </DetailRow>
                            )}
                            <DetailRow label="DFN:">{dfnOptions.find(o => o.value === roadblock.dfn)?.text || roadblock.dfn || '—'}</DetailRow>
                            <DetailRow label="Assigned To:">{roadblock.assignedToName || '—'}</DetailRow>
                            <DetailRow label="Production Impact:">{roadblock.productionImpact ? 'Yes' : 'No'}</DetailRow>
                            <DetailRow label="Time Spent to Resolve:">
                                {roadblock.leaderTimeSpent
                                    ? `${roadblock.leaderTimeSpent} ${TIME_UNIT_OPTIONS.find(o => o.value === roadblock.leaderTimeUnit)?.text ?? 'Hours'}`
                                    : '—'}
                            </DetailRow>
                            <DetailRow label="Submitted By:">{roadblock.submittedByName}</DetailRow>
                            <DetailRow label="Roadblock Description:" align="flex-start">
                                {roadblock.description}
                            </DetailRow>
                        </div>

                        {!isOpen && (
                            <>
                                <Divider />
                                <Header as="h5" style={{ marginBottom: '0.75rem', color: '#444' }}>Resolution</Header>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    <DetailRow label="Resolution Date:">{formatDateToMmDdYyyy(roadblock.resolutionDate)}</DetailRow>
                                    <DetailRow label="Resolution Note:" align="flex-start">
                                        {roadblock.resolutionNote || '—'}
                                    </DetailRow>
                                </div>
                            </>
                        )}
                    </>
                )}
            </Segment>

            {/* Notes section */}
            <Segment style={{ textAlign: 'left', padding: '2rem' }}>
                <Header as="h4">Notes</Header>

                {roadblock.notes?.length > 0 ? (
                    <CommentGroup>
                        {roadblock.notes.map(note => (
                            <Comment key={note.id}>
                                <CommentContent>
                                    <CommentAuthor>{note.authorName}</CommentAuthor>
                                    <CommentMetadata>
                                        {new Date(note.creationTime).toLocaleString()}
                                    </CommentMetadata>
                                    <CommentText>{note.text}</CommentText>
                                </CommentContent>
                            </Comment>
                        ))}
                    </CommentGroup>
                ) : (
                    <p style={{ color: '#666' }}>No notes yet.</p>
                )}

                <Divider />

                {noteError && <Message negative size="small">{noteError}</Message>}

                {!isReadOnly && (
                    <>
                        <Form>
                            <Form.Field>
                                <label>Add a Note</label>
                                <TextArea
                                    placeholder="Add a note..."
                                    maxLength={maxNote}
                                    value={noteText}
                                    onChange={(_, { value }) => setNoteText(value)}
                                    rows={3}
                                />
                                <div style={{ color: '#666', fontSize: '0.85em', marginTop: '0.25em', textAlign: 'right' }}>
                                    {noteText.length}/{maxNote} characters
                                </div>
                            </Form.Field>
                        </Form>
                        <Button
                            primary
                            size="small"
                            onClick={handleAddNote}
                            loading={savingNote}
                            disabled={savingNote || !noteText.trim()}
                        >
                            Add Note
                        </Button>
                    </>
                )}
            </Segment>

            <ResolveRoadblockModal
                open={showResolveModal}
                roadblockId={id}
                onClose={() => setShowResolveModal(false)}
                onResolved={handleResolved}
            />

            <DeleteRoadblockModal
                open={showDeleteModal}
                roadblockId={id}
                deleting={deleting}
                error={error}
                onClose={() => { setShowDeleteModal(false); setError(''); }}
                onConfirm={handleDelete}
            />
        </div>
    );
};
